import IntlMessages from '@crema/utility/IntlMessages'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import EditIcon from '@material-ui/icons/Edit'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import Scrollbar from '@crema/core/Scrollbar'
import { StringNumberArrayType } from 'components/Table/BasicTable'
import React, { useMemo } from 'react'
import { IOfferingResponse, IOfferingTagResponse } from 'types/api/temple'

interface OfferingTagTableProps {
    editHandler?: ((id: string | number) => void | Promise<any>) & Function
    deleteHandler?: ((id: string | number) => void | Promise<any>) & Function
    deletingIds?: StringNumberArrayType
    offeringTags: IOfferingTagResponse[]
    offerings: IOfferingResponse[]
}

type OfferingTagGroup = {
    id: string
    name: string
    tags: IOfferingTagResponse[]
}

const getOfferingLabel = (tag: IOfferingTagResponse, offerings: IOfferingResponse[]) => {
    const matchedOffering = offerings.find(offering => offering.url === tag.offering || String(offering.id) === String(tag.offering))
    return matchedOffering?.name ?? tag.offering
}

const getGroups = (offeringTags: IOfferingTagResponse[]): OfferingTagGroup[] => {
    const groupMap = new Map<string, OfferingTagGroup>()
    offeringTags.forEach(tag => {
        const tagName = tag.tagName ?? tag.name ?? 'Unknown Tag'
        const key = tagName.trim().toLowerCase()
        const group = groupMap.get(key)
        if (group) {
            group.tags.push(tag)
        } else {
            groupMap.set(key, {
                id: key,
                name: tagName,
                tags: [tag]
            })
        }
    })
    return Array.from(groupMap.values()).sort((a, b) => a.name.localeCompare(b.name))
}

const OfferingTagTable: React.FC<OfferingTagTableProps> = ({ editHandler, deleteHandler, deletingIds, offeringTags, offerings }) => {
    const groups = useMemo(() => getGroups(offeringTags), [offeringTags])
    const hasActions = Boolean(editHandler || deleteHandler)

    return (
        <TableContainer>
            <Scrollbar>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell size="small"><IntlMessages id="common.name" /></TableCell>
                            {hasActions && <TableCell size="small"></TableCell>}
                            <TableCell size="small"><IntlMessages id="offeringTag.offerings" /></TableCell>
                            <TableCell size="small"><IntlMessages id="offeringTag.assignedCount" /></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map(group => {
                            const isDeleting = deletingIds && deletingIds.indexOf(group.id) !== -1
                            return (
                                <TableRow key={group.id}>
                                    <TableCell component="th" scope="row" size="small">{group.name}</TableCell>
                                    {hasActions && (
                                        <TableCell size="small">
                                            <Box minWidth="max-content" width="100%" display="flex" flexDirection="row">
                                                {editHandler && (
                                                    <IconButton aria-label="edit" onClick={() => editHandler(group.id)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                                {deleteHandler && (
                                                    isDeleting ? (
                                                        <IconButton>
                                                            <CircularProgress size="1.5rem" />
                                                        </IconButton>
                                                    ) : (
                                                        <IconButton aria-label="delete" onClick={() => deleteHandler(group.id)}>
                                                            <RemoveCircleOutlineIcon />
                                                        </IconButton>
                                                    )
                                                )}
                                            </Box>
                                        </TableCell>
                                    )}
                                    <TableCell size="small">
                                        <Box display="flex" flexWrap="wrap">
                                            {group.tags.map(tag => (
                                                <Chip
                                                    key={tag.id}
                                                    label={getOfferingLabel(tag, offerings)}
                                                    size="small"
                                                    style={{ margin: 2 }}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell size="small">{group.tags.length}</TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </Scrollbar>
        </TableContainer>
    )
}

export default OfferingTagTable
